import React, { memo, useCallback, useMemo, useState } from 'react'
import { useStyles } from './styles'
import { Box, Checkbox, Grid, Paper, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import MultiStrikesExpiryMenu from './MultiStrikesExpiryMenu/MultiStrikesExpiryMenu'
import MultiStrikesMenu from './MultiStrikesMenu/MultiStrikesMenu'
import { useDispatch, useSelector } from 'react-redux'
import { selectMultiStrikeChartStyles } from '../../../selectors/multiStrikeChart'
import TimeBuckets from '../../../components/timeBuckets/TimeBuckets'
import { ChartViewKey } from '../../../assets/constants'
import { updateMultiStrikeChartConfig } from '../../../reducer/chart.reducer'
import { selectMultiStrikeChartConfigStyles } from '../../../selectors/chart'
import {
    thicknessButtons
} from '../../../components/multiStikeChart/chartWrapper/chartMetadata/configPopup/GroupOutputs'
import OutputPlotTypes from '../../../components/multiStikeChart/chartWrapper/chartMetadata/configPopup/OutputPlotTypes'
import { LineType } from 'lightweight-charts'
import MultiStrikeChartTypeMenu from './MultiStrikeChartTypeMenu'
import { NonInteractiveTooltip } from '../../../components/custom'
import SettingsOutlinedIcon from '../../../assets/icons/SettingOutlined'
import IconButton from '@mui/material/IconButton'
import ConfigurationPopup
    from '../../../components/multiStikeChart/chartWrapper/chartMetadata/configPopup/ConfigurationPopup'

export const NotifyInterval = [
    {
        label: '5s',
        value: 5
    },
    {
        label: '30s',
        value: 30
    },
    {
        label: '60s',
        value: 60
    }
]

const MultiStrikeHeader: React.FC<{ viewKey: string }> = ({ viewKey }) => {
    const { classes } = useStyles()
    const dispatch = useDispatch()
    const isWidget = useMemo(() => viewKey !== ChartViewKey.MULTI_STRIKES_CHART, [viewKey])
    const {
        lineThickness,
        lineType,
        autoRefresh,
        notifyInterval
    } = useSelector(isWidget ? selectMultiStrikeChartConfigStyles(viewKey) : selectMultiStrikeChartStyles)
    const [openSettings, setOpenSettings] = useState(false)

    const handleThicknessChange = useCallback((thickness: number) => {
        dispatch(updateMultiStrikeChartConfig({
            viewKey,
            config: {
                styles: {
                    lineThickness: thickness,
                    lineType,
                    autoRefresh,
                    notifyInterval
                }
            }
        }))
    }, [dispatch, lineType, viewKey, autoRefresh, notifyInterval])

    const handleNotifyIntervalChange = useCallback((interval: number) => {
        dispatch(updateMultiStrikeChartConfig({
            viewKey,
            config: {
                styles: {
                    autoRefresh,
                    lineType,
                    lineThickness,
                    notifyInterval: interval
                }
            }
        }))
    }, [dispatch, viewKey, lineType, lineThickness, autoRefresh])

    const handleAutoRefresh = useCallback((autoRefresh: boolean) => {
        dispatch(updateMultiStrikeChartConfig({
            viewKey,
            config: {
                styles: {
                    autoRefresh: autoRefresh,
                    lineType,
                    lineThickness,
                    notifyInterval
                }
            }
        }))
    }, [dispatch, viewKey, lineType, lineThickness, notifyInterval])

    const handlePlotTypeChange = useCallback((plotType: LineType) => {
        dispatch(updateMultiStrikeChartConfig({
            viewKey,
            config: {
                styles: {
                    lineThickness,
                    lineType: plotType,
                    autoRefresh,
                    notifyInterval
                }
            }
        }))
    }, [dispatch, lineThickness, viewKey, autoRefresh, notifyInterval])

    return !isWidget ? (
        <>
            <div className={classes.item}>
                <MultiStrikesExpiryMenu viewKey={viewKey} />
            </div>
            <div className={classes.item}>
                <MultiStrikesMenu viewKey={viewKey} />
            </div>
            <TimeBuckets
                id={'multstrike-header-time-bucket'}
                viewKey={viewKey}
                registerShortcut={false}
                multiStrikeTimebucket={!isWidget}
                showFavorites={false}
                buttonProps={{
                    sx: {
                        height: '32px',
                        width: '35px',
                        marginLeft: '8px',
                        borderColor: theme => theme.palette.background.default,
                        '.MuiTypography-root': {
                            fontWeight: '500 !important'
                        }
                    },
                    variant: 'outlined'
                }}
            />
            <MultiStrikeChartTypeMenu isWidget={isWidget} viewKey={viewKey} />
            <NonInteractiveTooltip title="Settings" arrow placement="top">
                <IconButton data-event={'Settings Clicked'} data-event-source={'multistrike-stockbar'}
                            id={'multistrike-header-settings-button'} sx={{ marginLeft: '8px' }}
                            onClick={() => setOpenSettings(true)}>
                    <SettingsOutlinedIcon fontSize={"small"} />
                </IconButton>
            </NonInteractiveTooltip>
            <ConfigurationPopup open={openSettings} onClose={() => setOpenSettings(false)} chartId={viewKey} />
        </>
    ) : (
        <Grid container gap={1} sx={{ width: '450px' }}>
            <Grid container gap={2} alignItems={'center'}>
                <Grid item style={{ width: '120px' }}>Select Expiry</Grid>
                <Grid item>
                    <MultiStrikesExpiryMenu viewKey={viewKey} />
                </Grid>
            </Grid>
            <Grid container gap={2} alignItems={'center'}>
                <Grid item style={{ width: '120px' }}>Select Strikes</Grid>
                <Grid item>
                    <MultiStrikesMenu viewKey={viewKey} />
                </Grid>
            </Grid>
            <Grid container gap={2} alignItems={'center'}>
                <Grid item style={{ width: '120px' }}>Chart Type</Grid>
                <Grid item>
                    <MultiStrikeChartTypeMenu isWidget={isWidget} viewKey={viewKey} />
                </Grid>
            </Grid>
            <Grid container gap={2} alignItems={'center'}>
                <Grid item style={{ width: '120px' }}>Thickness</Grid>
                <Grid item sx={{ height: 32, display: 'flex', alignItems: 'center' }}>
                    <ToggleButtonGroup size="small" value={lineThickness} exclusive
                                       onChange={(e, val) => handleThicknessChange(val)} orientation="horizontal"
                                       sx={{ height: '25px' }}>
                        {thicknessButtons()}
                    </ToggleButtonGroup>
                </Grid>
            </Grid>
            <Grid container gap={2} alignItems={'center'}>
                <Grid item style={{ width: '120px' }}>Plot Type</Grid>
                <Grid item style={{ height: '32px', display: 'flex', alignItems: 'center' }}>
                    <OutputPlotTypes lineType={lineType} onSelected={handlePlotTypeChange} />
                </Grid>
            </Grid>
            <Typography my={0.5} color="textSecondary" variant={'caption'}>ATM Update</Typography>
            <Grid gap={2} container key="autoRefresh" sx={{ alignItems: 'center' }}>
                <Grid item style={{ width: '120px' }}>Auto</Grid>
                <Grid item xs={8}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                            disableRipple={true}
                            sx={{
                                padding: 0
                            }}
                            checked={autoRefresh}
                            onChange={(e, checked) => {
                                handleAutoRefresh(checked)
                            }}
                        />
                        <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                            (ATM will update every 30 sec)
                        </Typography>
                    </Box>
                </Grid>
            </Grid>
            <Grid container gap={2} alignItems={'center'} marginTop={1}>
                <Grid item style={{ width: '120px' }}>Notify</Grid>
                <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
                    <Stack direction={'row'} spacing={1} alignItems={'center'}>
                        <ToggleButtonGroup
                            disabled={autoRefresh}
                            size="small"
                            value={notifyInterval}
                            exclusive
                            onChange={(e, val) => handleNotifyIntervalChange(val)}
                            orientation="horizontal"
                            sx={{ height: '24px' }}
                            aria-label="Notification Interval"
                        >
                            {NotifyInterval.map((interval) => (
                                <ToggleButton key={interval.value} value={interval.value} aria-label={interval.label}>
                                    {interval.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                        <Typography textAlign={'justify'} variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                            (Get notified if ATM changes)
                        </Typography>
                    </Stack>
                </Grid>
            </Grid>
        </Grid>
    )
}

export default memo(MultiStrikeHeader)